import React from "react";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import {
  // prostate9yearCase,
  year9CaseProstate20220511,
  newProstateAgeChecker,
  newDefaultProstateAgeChecker,
  // heightChecker,
} from "../../../utils/calculate";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const useStyles = makeStyles((theme) => ({
  bold: {
    fontWeight: 600,
    [theme.breakpoints.down("md")]: {
      fontSize: 16,
    },
  },
  info: {
    display: "flex",
    alignItems: "center",
    margin: "8px 0px",
    color: "#1a2633",
    width: 120,
    [theme.breakpoints.down("md")]: {
      width: 100,
      paddingLeft: 20,
    },
    [theme.breakpoints.down("sm")]: {
      width: 80,
    },
  },
  infoLine: {
    flex: 1,
    background: "#60768e",
    height: 1,
    fontSize: 0,
    lineHeight: 0,
    margin: "0px 16px",
  },
  body: {
    width: "100%",
    marginTop: 32,
    display: "none",
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
      paddingLeft: 4,
      paddingRight: 4,
      display: "block",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      marginBottom: 12,
      flexDirection: "column",
    },
  },
  body2: {
    fontSize: 19,
    marginTop: 32,
    marginBottom: 32,
    width: "100%",
    [theme.breakpoints.down("md")]: {
      paddingLeft: 20,
      paddingRight: 20,
      display: "none",
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 12,
      flexDirection: "column",
    },
  },
  bodySub: {
    [theme.breakpoints.down("md")]: {
      paddingLeft: 20,
      paddingRight: 20,
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 12,
    },
  },
  bodybol: {
    marginBottom: 32,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  bodybol2: {
    flex: 1,
    marginBottom: 32,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  inputLine: {
    width: "50%",
    color: "#1a2633",
  },
  buttons: {
    float: "left",
    "& > .on": {
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    "& > div": {
      display: "inline-block",
      fontSize: 18,
      width: 80,
      height: 36,
      marginRight: 16,
      cursor: "pointer",
      textAlign: "center",
      lineHeight: 2.1,
      color: "#b1b1b1",
      borderRadius: 3,
      border: "1px solid #b1b1b1",
    },
  },
  icon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  button: {
    width: 432,
    height: 78,
    fontSize: 20,
    [theme.breakpoints.down("sm")]: {
      width: 300,
      height: 56,
      fontSize: 18,
    },
  },
  sex: {
    paddingRight: 6,
  },
  viewtitle: {
    color: "#60768e",
  },
  title1: {
    width: 65,
    color: "#60768e",
  },
  title2: {
    width: 120,
    color: "#60768e",
  },
  half: {
    width: "50%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  half2: {
    width: "100%",
  },
  content: {
    marginTop: 32,
    minHeight: 60,
    marginBottom: 60,
  },
  subCon: {
    marginTop: 40,
    marginBottom: 40,
    [theme.breakpoints.down("md")]: {
      paddingLeft: 20,
      paddingRight: 20,
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: 40,
    },
  },

  em: {
    color: "#10459a",
  },
  contentGraph1: {
    width: 46,
    height: 24,
  },
  colorred: {
    background: "#ff8686",
  },
  colororg: {
    background: "#ffb986",
  },
  colorblu: {
    background: "#03a9f4",
  },
  contentGraphRight: {
    width: 100,
    textAlign: "center",
  },
  titleName: {
    marginBottom: 32,
  },
  subtitle: {
    minHeight: 48,
    lineHeight: 1.5,
  },
  subLineHeight: {
    fontSize: 19,
    lineHeight: 1.6,
    [theme.breakpoints.down("md")]: {
      fontSize: 22,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 17,
    },
  },
  pan: {
    height: 130,
    width: 310,
    backgroundColor: "rgb(255 255 255 / 50%)",
    padding: 8,
    border: "1px solid #b2b4b7",
  },
}));

const CustomTooltip = ({ active, payload, label, cancer }) => {
  const classes = useStyles();
  const textSubCheck = (label = "위험요인이 없는 경우 발생확률") => {
    let text = "";
    if (label === "위험요인이 없는 경우 발생확률") {
      text = `같은 연령, 성별을 가진, 흡연 등 위험요인이\n없는 분의 향후 9년간의 ${cancer} 발생 위험`;
    } else if (label === "비슷한 연령대 평균 발생률") {
      text = `같은 연령, 성별을 가진 평균적인 분의\n
      향후 9년간의 ${cancer} 발생 위험`;
    } else {
      text = `귀하의 향후 9년간의 ${cancer} 발생 위험`;
    }
    return text;
  };
  const payloadData = (payload, label) => {
    return payload[0].payload[label];
  };
  if (active && payload && payload.length) {
    return (
      <Grid container direction="column" className={classes.pan}>
        <Typography variant="h4">
          <span className={classes.bold}>{`${label} `}</span> :{" "}
          <span className={clsx(classes.em, classes.bold)}>
            {payloadData(payload, label)}%
          </span>
        </Typography>
        <br />
        <Typography>{textSubCheck(label)}</Typography>
      </Grid>
    );
  }

  return null;
};

const CustomTooltipSmall = ({ active, payload, label, cancer }) => {
  const classes = useStyles();

  const textSubCheck = (label = "위험요인이 없는 경우") => {
    let text = "";
    if (label === "위험요인이 없는 경우") {
      text = `같은 연령, 성별을 가진, 흡연 등 위험요인이\n없는 분의 향후 9년간의 ${cancer} 발생 위험`;
    } else if (label === "같은 나이/성별") {
      text = `같은 연령, 성별을 가진 평균적인 분의\n
      향후 9년간의 ${cancer} 발생 위험`;
    } else {
      text = `귀하의 향후 9년간의 ${cancer} 발생 위험`;
    }
    return text;
  };

  const payloadData = (payload, label) => {
    return payload[0].payload[label];
  };

  if (active && payload && payload.length) {
    return (
      <Grid container direction="column" className={classes.pan}>
        <Typography variant="h4">
          <span className={classes.bold}>{`${label} `}</span> :{" "}
          <span className={clsx(classes.em, classes.bold)}>
            {payloadData(payload, label)}%
          </span>
        </Typography>
        <br />
        <Typography>{textSubCheck(label)}</Typography>
      </Grid>
    );
  }

  return null;
};

const FormList = ({
  cancer = "전립선암",
  onLinkHome,
  score,
  total,
  onChange,
  data,
}) => {
  const classes = useStyles();

  const meyper = year9CaseProstate20220511(total);
  const meAgeAroundPer = newProstateAgeChecker(data.age, data.sex);
  const meAgedefaultAroundPer = newDefaultProstateAgeChecker(
    data.age,
    data.sex
  );

  const datass = [
    {
      name: `9년 내 ${cancer} 발생 위험도`,
      [`9년 내 ${cancer} 발생 위험도`]: meyper,
      "비슷한 연령대 평균 발생률": 0,
      "위험요인이 없는 경우 발생확률": 0,
    },
    {
      name: "비슷한 연령대 평균 발생률",
      [`9년 내 ${cancer} 발생 위험도`]: 0,
      "비슷한 연령대 평균 발생률": meAgeAroundPer,
      "위험요인이 없는 경우 발생확률": 0,
    },
    {
      name: "위험요인이 없는 경우 발생확률",
      [`9년 내 ${cancer} 발생 위험도`]: 0,
      "비슷한 연령대 평균 발생률": 0,
      "위험요인이 없는 경우 발생확률": meAgedefaultAroundPer,
    },
  ];

  const datasSmall = [
    {
      name: `귀하`,
      [`귀하`]: meyper,
      "같은 나이/성별": 0,
      "위험요인이 없는 경우": 0,
    },
    {
      name: "같은 나이/성별",
      [`귀하`]: 0,
      "같은 나이/성별": meAgeAroundPer,
      "위험요인이 없는 경우": 0,
    },
    {
      name: "위험요인이 없는 경우",
      [`귀하`]: 0,
      "같은 나이/성별": 0,
      "위험요인이 없는 경우": meAgedefaultAroundPer,
    },
  ];

  return (
    <>
      <Grid container justify="space-between" alignItems="center">
        <Grid className={classes.info}>
          <Typography variant="h3">위험도</Typography>
        </Grid>
        <Grid className={classes.infoLine}></Grid>
      </Grid>
      <Grid
        container
        alignItems="center"
        className={classes.body2}
        style={{ height: "500px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            // width={500}
            // height={300}
            data={datass}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              padding={{ top: 100 }}
              tick={{ strokeWidth: 1 }}
              type="number"
              unit="%"
            />
            <Tooltip
              content={<CustomTooltip cancer={cancer} />}
              cursor={{ fill: "rgb(158 158 158 / 4%)" }}
            />
            <Bar
              dataKey={`9년 내 ${cancer} 발생 위험도`}
              stackId="a"
              barSize={30}
              fill="#ff8686"
            />
            <Bar
              dataKey="비슷한 연령대 평균 발생률"
              stackId="a"
              fill="#ffb986"
              barSize={30}
            />
            <Bar
              dataKey="위험요인이 없는 경우 발생확률"
              stackId="a"
              barSize={30}
              fill="#03a9f4"
            />
          </BarChart>
        </ResponsiveContainer>
      </Grid>
      <Grid
        container
        className={classes.body}
        justify="space-between"
        direction="row"
        style={{ height: "300px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={"100%"}
            height={300}
            data={datasSmall}
            margin={{
              top: 20,
              // right: 30,
              // left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} />
            {/* <YAxis
              padding={{ top: 100 }}
              tick={{ strokeWidth: 1 }}
              type="number"
              unit="%"
            /> */}
            <Tooltip
              content={<CustomTooltipSmall cancer={cancer} />}
              cursor={{ fill: "rgb(158 158 158 / 4%)" }}
            />
            <Bar dataKey={`귀하`} stackId="a" barSize={20} fill="#ff8686" />
            <Bar
              dataKey="같은 나이/성별"
              stackId="a"
              fill="#ffb986"
              barSize={20}
            />
            <Bar
              dataKey="위험요인이 없는 경우"
              stackId="a"
              barSize={20}
              fill="#03a9f4"
            />
          </BarChart>
        </ResponsiveContainer>
        {/* <Grid
          className={classes.bodybol2}
          container
          justify="space-between"
          direction="column"
        >
          <Grid className={classes.half2}>
            <Grid container direction="column">
              <Typography variant="h4" className={classes.titleName}>
                <span className={clsx(classes.bold, classes.viewtitle)}>
                  9년 내 {cancer} 발생 위험도
                </span>
              </Typography>
              <Typography
                variant="h4"
                color="textSecondary"
                className={classes.subtitle}
              >
                <span className={clsx(classes.viewtitle)}>
                  귀하의 향후 9년간의 {cancer} 발생 위험
                </span>
              </Typography>
              <Grid container alignItems="center" className={classes.content}>
                <Grid
                  className={clsx(classes.contentGraph1, classes.colorred)}
                  style={{ height: heightChecker(meyper) }}
                ></Grid>
                <Grid className={classes.contentGraphRight}>
                  <Typography variant="h4">
                    <span className={clsx(classes.em, classes.bold)}>
                      {meyper}%
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          className={classes.bodybol2}
          container
          justify="space-between"
          alignItems="center"
        >
          <Grid className={classes.half2}>
            <Grid container direction="column">
              <Typography
                variant="h4"
                color="textSecondary"
                className={classes.titleName}
              >
                <span className={clsx(classes.bold)}>
                  비슷한 연령대 평균 발생률
                </span>
              </Typography>
              <Typography variant="h4">
                <span className={clsx(classes.viewtitle)}>
                  같은 연령, 성별을 가진 평균적인 분의
                  <br />
                  향후 9년간의 {cancer} 발생위험
                </span>
              </Typography>
              <Grid container alignItems="center" className={classes.content}>
                <Grid
                  className={clsx(classes.contentGraph1, classes.colororg)}
                  style={{ height: heightChecker(meAgeAroundPer) }}
                ></Grid>
                <Grid className={classes.contentGraphRight}>
                  <Typography variant="h4">
                    <span className={clsx(classes.em, classes.bold)}>
                      {meAgeAroundPer}%
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid className={classes.half2}>
            <Grid container direction="column">
              <Typography
                variant="h4"
                color="textSecondary"
                className={classes.titleName}
              >
                <span className={clsx(classes.bold)}>
                  위험요인이 없는 경우 발생확률
                </span>
              </Typography>
              <Typography variant="h4">
                <span className={clsx(classes.viewtitle)}>
                  같은 연령, 성별을 가진, 흡연 등 위험요인이
                  <br />
                  없는 분의 향후 9년간의 {cancer} 발생 위험
                </span>
              </Typography>
              <Grid container alignItems="center" className={classes.content}>
                <Grid
                  className={clsx(classes.contentGraph1, classes.colorblu)}
                  style={{ height: heightChecker(meAgedefaultAroundPer) }}
                ></Grid>
                <Grid className={classes.contentGraphRight}>
                  <Typography variant="h4">
                    <span className={clsx(classes.em, classes.bold)}>
                      {meAgedefaultAroundPer}%
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid> */}
      </Grid>
      <Grid container justify="space-between" alignItems="center">
        <Grid className={classes.info}>
          <Typography variant="h3">결과 해석</Typography>
        </Grid>
        <Grid className={classes.infoLine}></Grid>
      </Grid>
      <Grid container className={classes.subCon}>
        <Typography variant="h4" className={classes.subLineHeight}>
          귀하에게 9년 안에 {cancer}이 발생할 확률은&nbsp;
          <span className={clsx(classes.bold, classes.em)}>{meyper}%</span>
          입니다.
          <br />
          귀하와 같은 나이의 평균적인 남성에서 향후 9년간 {cancer}{" "}
          발생확률은&nbsp;
          <span className={clsx(classes.bold, classes.em)}>
            {meAgeAroundPer}%
          </span>
          입니다.
          <br />
          귀하와 같은 나이의 남성에서 다른 위험요인이 없는경우&nbsp;
          {cancer}이 발생할 확률은&nbsp;
          <span className={clsx(classes.bold, classes.em)}>
            {meAgedefaultAroundPer}%
          </span>
          입니다.
        </Typography>
      </Grid>
    </>
  );
};

export default FormList;
